import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { InputWrapper, TextLink } from "../../SharableStyles/FormStyle";
import Button from "../sharable/ActionButton";
import Loading from "../sharable/Loading";
import Logo from "../sharable/Logo";
import OAuth from "../sharable/OAuth";
import { emailRegex, redirectAfterAuthOrDefault } from "../../services/Utils";
import { setTokenCookie } from "../../TokenCookie";
import FormMessage from "../sharable/FormMessage";
import Help from "../sharable/Help";
import PasswordToggle from "../sharable/hooks/PasswordToggle";
import useFormValidation from "../sharable/hooks/useFormValidation";
import { getRegisterThemePopup } from "../../OBJRep/RegisterThemes";
import {
  PageWrapper,
  CompactHeader,
  MainGrid,
  MarketingColumn,
  FormColumn,
  HeroHeading,
  HeroSubtext,
  HeroCtaWrapper,
  StepsTitle,
  StepsGrid,
  StepCard,
  StepTopRow,
  StepNumber,
  StepIcon,
  ConversionBlock,
  ThemePanel,
  SeloImagesWrapper,
  SeloImage,
  FormCard,
  FormCardHeader,
  TrialBadge,
  GoogleButtonWrapper,
  OrDivider,
  FieldGroup,
  StyledInput,
  PasswordFieldWrapper,
  FooterNote,
} from "./Register.styles";

const BASE_URL = process.env.REACT_APP_SERVER_BASE_URL;

export default function RegisterPage() {
  const [registering, setRegistering] = useState();
  const [errorMessage, setErrorMessage] = useState();
  const [showPassword, setShowPassword] = useState();
  const [showOnlyPhoneForGoogle, setShowOnlyPhoneForGoogle] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const themePopup = getRegisterThemePopup(searchParams.get("tema"));

  const phoneErrorMessage =
    "Número deve ter 11 digítos, incluindo o DDD e 9 inicial: 77 9 00000000";

  const normalizePhone = (value) => String(value || "").replace(/\D/g, "");

  const formatPhone = (value) => {
    const digits = normalizePhone(value).slice(0, 11);
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  };

  const { values, handleChange, errors, validate, setErrors } =
    useFormValidation({
      name: {
        label: "nome",
        required: true,
        minLength: 3,
        maxLength: 100,
        customMessages: {
          minLength: "Seu nome deve conter pelo menos 3 caracteres",
          maxLength: "Seu nome deve conter no máximo 100 caracteres",
        },
      },
      email: {
        label: "email",
        required: true,
        regex: emailRegex,
        customMessages: {
          regex: "Esse email é inválido.",
        },
      },
      password: {
        label: "senha",
        required: true,
        minLength: 6,
        maxLength: 100,
        customMessages: {
          minLength: "Sua senha deve ter no mínimo 6 dígitos.",
          maxLength: "Sua senha deve ter no máximo 100 dígitos.",
        },
      },
      phone: {
        label: "telefone",
        required: true,
        regex: "^\\d{11}$",
        customMessages: {
          regex: phoneErrorMessage,
        },
      },
    });

  const canProceedWithGoogleRegister = () => {
    const normalizedPhone = normalizePhone(values.phone);
    if (/^\d{11}$/.test(normalizedPhone)) {
      setShowOnlyPhoneForGoogle(false);
      setErrors((prev) => ({ ...prev, phone: [] }));
      return true;
    }

    setShowOnlyPhoneForGoogle(true);
    setErrors((prev) => ({ ...prev, phone: [phoneErrorMessage] }));
    return false;
  };

  const register = async () => {
    if (!validate()) return;
    const body = {
      email: values.email,
      password: values.password,
      username: values.name,
      phone: normalizePhone(values.phone),
    };
    setRegistering(true);
    setErrorMessage(undefined);
    let res;
    try {
      res = await fetch(`${BASE_URL}auth/basic/register`, {
        method: "POST",
        body: JSON.stringify(body),
        headers: {
          "Content-Type": "application/json",
        },
      });
    } catch (err) {
      setRegistering(false);
      setErrorMessage("Sem conexão. Tente novamente.");
      return;
    }
    setRegistering(false);
    if (res.ok) {
      const token = await res.text();
      setTokenCookie(token);
      redirectAfterAuthOrDefault(navigate, "/encartes?autoCreateEncarte=true");
    } else if (res.status === 409) {
      setErrorMessage(
        "Esse email já está sendo utilizado. Tente fazer login. Se tiver com dúvida, entre em contato com o suporte.",
      );
    } else {
      setErrorMessage(
        "Não foi possível criar a conta. Tente novamente ou entre em contato com o suporte.",
      );
    }
  };
  return (
    <PageWrapper>
      <CompactHeader>
        <Logo navigable={true} size={9} />
      </CompactHeader>

      <MainGrid>
        <MarketingColumn>
          <div>
            <HeroHeading>
              Suas Ofertas. Seu encarte.
              <br />
              <span>Pronto em minutos.</span>
            </HeroHeading>
            <HeroSubtext>
              Feito para quem vende no varejo e precisa divulgar suas ofertas de
              forma rápida e profissional. 🛒
            </HeroSubtext>
            <HeroCtaWrapper>
              <Button
                text={"Começar grátis"}
                $fullWidth
                action={() => {
                  document
                    .getElementById("cadastro-form")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
              />
            </HeroCtaWrapper>
          </div>

          <div>
            <StepsTitle>
              Da lista de produtos ao encarte em 3 passos.
            </StepsTitle>
            <StepsGrid>
              <StepCard>
                <StepTopRow>
                  <StepNumber>01</StepNumber>
                  <StepIcon icon="icons/list-icon.png" />
                </StepTopRow>
                <h3>Cole sua lista</h3>
                <p>Adicione seus produtos e preços.</p>
              </StepCard>
              <StepCard>
                <StepTopRow>
                  <StepNumber>02</StepNumber>
                  <StepIcon icon="icons/choose-icon.png" />
                </StepTopRow>
                <h3>Escolha o tema</h3>
                <p>Selecione um tema para sua oferta.</p>
              </StepCard>
              <StepCard>
                <StepTopRow>
                  <StepNumber>03</StepNumber>
                  <StepIcon icon="icons/download-icon.png" />
                </StepTopRow>
                <h3>Baixe seu encarte</h3>
                <p>Pronto. Agora é só divulgar.</p>
              </StepCard>
            </StepsGrid>
          </div>
          {/* 

          <ConversionBlock>
            <h2>Crie seu primeiro encarte.</h2>
            <p>
              Comece grátis e veja como é simples transformar suas ofertas em
              divulgação profissional.
            </p>
            <Button
              text={"Começar grátis"}
              action={() => {
                document
                  .getElementById("cadastro-form")
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
            />
          </ConversionBlock>
*/}
          {themePopup && (
            <ThemePanel>
              {themePopup.selos?.some(Boolean) && (
                <SeloImagesWrapper>
                  {themePopup.selos.filter(Boolean).map((selo, i) => (
                    <SeloImage src={selo} alt="" key={i} />
                  ))}
                </SeloImagesWrapper>
              )}
              <h2>{themePopup.titulo}</h2>
              <p className="subtitulo">{themePopup.subtitulo}</p>
            </ThemePanel>
          )}
        </MarketingColumn>

        <FormColumn id="cadastro-form">
          <FormCard>
            {registering && <Loading />}
            <FormCardHeader>
              <h1>Crie seu primeiro encarte</h1>
              <p>
                Transforme sua lista de produtos e preços em um encarte
                profissional para divulgar suas ofertas.
              </p>
            </FormCardHeader>

            <TrialBadge>
              TESTE GRÁTIS
              <span className="no-card">
                Você não precisa de cartão de crédito😉
              </span>
            </TrialBadge>

            <GoogleButtonWrapper>
              <OAuth
                setErrorMessage={setErrorMessage}
                action={"register"}
                setLogging={setRegistering}
                phone={normalizePhone(values.phone)}
                canProceedWithGoogleRegister={canProceedWithGoogleRegister}
                message={
                  "Crie sua conta utilizando sua conta do Google! É fácil e rápido!"
                }
                buttonTxt={"Continuar com Google"}
              />
            </GoogleButtonWrapper>

            {!showOnlyPhoneForGoogle && (
              <OrDivider>ou cadastre-se com seu e-mail</OrDivider>
            )}

            {errorMessage && <FormMessage message={errorMessage} />}

            <InputWrapper style={{ padding: 0, gap: "1.6rem" }}>
              {!showOnlyPhoneForGoogle && (
                <>
                  <FieldGroup>
                    <label htmlFor="name">Nome</label>
                    <StyledInput
                      id="name"
                      placeholder="Seu nome"
                      onInput={(e) => handleChange("name", e.target.value)}
                      autoComplete="name"
                      $hasError={errors.name?.length > 0}
                    />
                    {errors.name &&
                      errors.name.map((msg, i) => (
                        <FormMessage
                          message={msg}
                          key={i}
                          backgroundColor="transparent"
                          align="left"
                        />
                      ))}
                  </FieldGroup>

                  <FieldGroup>
                    <label htmlFor="email">E-mail</label>
                    <StyledInput
                      id="email"
                      placeholder="Seu email"
                      type="email"
                      onInput={(e) => handleChange("email", e.target.value)}
                      autoComplete="email"
                      $hasError={errors.email?.length > 0}
                    />
                    {errors.email &&
                      errors.email.map((msg, i) => (
                        <FormMessage
                          message={msg}
                          key={i}
                          backgroundColor="transparent"
                          align="left"
                        />
                      ))}
                  </FieldGroup>

                  <FieldGroup>
                    <label htmlFor="password">Senha</label>
                    <StyledInput
                      id="password"
                      placeholder="Sua senha"
                      type={showPassword ? "text" : "password"}
                      onInput={(e) => handleChange("password", e.target.value)}
                      autoComplete="new-password"
                      $hasError={errors.password?.length > 0}
                    />
                    <PasswordFieldWrapper>
                      <PasswordToggle
                        password={values.password}
                        showPassword={showPassword}
                        setShowPassword={setShowPassword}
                      />
                    </PasswordFieldWrapper>
                    {errors.password &&
                      errors.password.map((msg, i) => (
                        <FormMessage
                          message={msg}
                          key={i}
                          backgroundColor="transparent"
                          align="left"
                        />
                      ))}
                  </FieldGroup>
                </>
              )}

              <FieldGroup>
                <label htmlFor="phone">WhatsApp</label>
                <StyledInput
                  id="phone"
                  placeholder="(00) 00000-0000"
                  type="tel"
                  inputMode="numeric"
                  value={formatPhone(values.phone || "")}
                  onInput={(e) => {
                    const normalizedPhone = normalizePhone(e.target.value);
                    handleChange("phone", normalizedPhone);
                    if (
                      showOnlyPhoneForGoogle &&
                      /^\d{11}$/.test(normalizedPhone)
                    ) {
                      setShowOnlyPhoneForGoogle(false);
                    }
                  }}
                  autoComplete="tel"
                  $hasError={errors.phone?.length > 0}
                />
                {errors.phone &&
                  errors.phone.map((msg, i) => (
                    <FormMessage
                      message={msg}
                      key={i}
                      backgroundColor="transparent"
                      align="left"
                    />
                  ))}
              </FieldGroup>

              {!showOnlyPhoneForGoogle && (
                <Button
                  text={"CRIAR MEU ENCARTE"}
                  $fullWidth
                  action={() => {
                    register();
                  }}
                />
              )}

              <TextLink>
                Já possui uma conta? <Link to="/entrar">Entre</Link>
              </TextLink>
            </InputWrapper>
          </FormCard>
        </FormColumn>
      </MainGrid>

      <Help showGroup={false} showYoutube={false} />
      <FooterNote>
        © {new Date().getFullYear()} OfertasExpress. Todos os direitos
        reservados.
      </FooterNote>
    </PageWrapper>
  );
}
